import React from 'react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

const fmtDateTime = (iso, timeZone) => {
	if (!iso) return '—'
	return new Date(iso).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		...(timeZone ? { timeZone } : {}),
	})
}

const fmtTime = (iso, timeZone) =>
	new Date(iso).toLocaleString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		...(timeZone ? { timeZone } : {}),
	})

// Short timezone abbreviation (EDT, CDT, …) for the given IANA zone.
const tzAbbr = (iso, timeZone) => {
	if (!timeZone || !iso) return ''
	const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
		.formatToParts(new Date(iso))
		.find(p => p.type === 'timeZoneName')
	return part ? ` ${part.value}` : ''
}

const sameDayInZone = (a, b, timeZone) => {
	const fmt = new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		...(timeZone ? { timeZone } : {}),
	})
	return fmt.format(new Date(a)) === fmt.format(new Date(b))
}

const fmtApptWindow = (start, end, timeZone) => {
	if (!start && !end) return '—'
	if (!start) return `${fmtDateTime(end, timeZone)}${tzAbbr(end, timeZone)}`
	if (!end || start === end) return `${fmtDateTime(start, timeZone)}${tzAbbr(start, timeZone)}`
	if (sameDayInZone(start, end, timeZone))
		return `${fmtDateTime(start, timeZone)} – ${fmtTime(end, timeZone)}${tzAbbr(end, timeZone)}`
	return `${fmtDateTime(start, timeZone)} – ${fmtDateTime(end, timeZone)}${tzAbbr(end, timeZone)}`
}

const fmtWeightNum = w => {
	const n = Number(w)
	if (!Number.isFinite(n) || n <= 0) return null
	return n.toLocaleString()
}

export const SOURCE_DETAIL_URL = {
	LANDSTAR: load => {
		const {
			loadId,
			landstarAgencyCode,
			landstarSourceSystemId,
			landstarCompanyCode,
			landstarOriginMiles,
		} = load
		if (!loadId || !landstarAgencyCode || !landstarSourceSystemId || !landstarCompanyCode) {
			return null
		}
		const params = new URLSearchParams({
			LoadID: loadId,
			agencyCode: landstarAgencyCode,
			sourceSystemID: landstarSourceSystemId,
			companyCode: landstarCompanyCode,
			originMiles: String(landstarOriginMiles ?? 0),
		})
		return `https://www.landstaronline.com/LoadsPublic/Load/LoadDetails?${params}`
	},
}

function StopCard({ stop, tokens, isLight, timeZone }) {
	const isPickup = String(stop.type || '').toLowerCase() === 'pickup'
	const dotCls = isPickup
		? isLight
			? 'bg-emerald-500'
			: 'bg-emerald-400'
		: isLight
			? 'bg-sky-500'
			: 'bg-sky-400'
	const flagCls = isLight ? 'bg-gray-200 text-gray-700' : 'bg-white/10 text-gray-300'
	const weight = fmtWeightNum(stop.stopWeight)

	return (
		<div className="flex gap-3">
			<div className="flex flex-col items-center pt-1">
				<span className={`w-2 h-2 rounded-full ${dotCls}`} />
				<span className={`mt-1 text-[10px] ${tokens.valueMuted}`}>
					{stop.sequence ?? ''}
				</span>
			</div>
			<div className="flex-1 min-w-0">
				<div className="flex items-baseline gap-2 flex-wrap">
					<span
						className={`text-[11px] font-semibold uppercase tracking-wider ${tokens.valueMuted}`}
					>
						{stop.type || 'Stop'}
					</span>
					<span className={`text-[12px] font-semibold ${tokens.valueStrong}`}>
						{stop.city}
						{stop.state ? `, ${stop.state}` : ''}
						{stop.zip ? ` ${stop.zip}` : ''}
					</span>
					{stop.apptType && (
						<span className={`text-[10px] rounded px-1.5 py-px ${flagCls}`}>{stop.apptType}</span>
					)}
					{stop.dropTrailer && (
						<span className={`text-[10px] rounded px-1.5 py-px ${flagCls}`}>Drop trailer</span>
					)}
					{stop.pickupTrailer && (
						<span className={`text-[10px] rounded px-1.5 py-px ${flagCls}`}>Pickup trailer</span>
					)}
				</div>
				<div className={`mt-0.5 text-[11px] ${tokens.valueDim}`}>
					{fmtApptWindow(stop.apptStart, stop.apptEnd, timeZone)}
				</div>
				<div
					className={`mt-0.5 flex flex-wrap gap-x-3 text-[10.5px] ${tokens.valueMuted}`}
				>
					{weight && <span>{weight} lbs</span>}
					{stop.quantity != null && stop.quantity !== 0 && (
						<span>
							{stop.quantity}
							{stop.units ? ` ${String(stop.units).toLowerCase()}` : ''}
						</span>
					)}
					{stop.loadingType && <span>{String(stop.loadingType).toLowerCase()}</span>}
					{stop.facilityRating != null && <span>★ {stop.facilityRating}</span>}
					{stop.facilityWaitMin > 0 && <span>~{stop.facilityWaitMin}m wait</span>}
					{stop.milesToNextStop > 0 && (
						<span>→ {Number(stop.milesToNextStop).toLocaleString()} mi to next</span>
					)}
				</div>
			</div>
		</div>
	)
}

function LoadExpandedRow({ load, tokens, isLight }) {
	const Block = ({ title, children }) => (
		<div>
			<div
				className={`text-[10px] font-semibold tracking-wider uppercase mb-1.5 ${tokens.valueMuted}`}
			>
				{title}
			</div>
			<div className="space-y-1">{children}</div>
		</div>
	)

	const Row = ({ label, value, mono }) => (
		<div className="flex items-baseline gap-2 text-[12px]">
			<span className={`${tokens.valueMuted} w-16 shrink-0`}>{label}</span>
			<span className={`${tokens.valueDim} ${mono ? 'text-[11px]' : ''} truncate`}>
				{value || '—'}
			</span>
		</div>
	)

	const expandedBg = isLight ? 'bg-gray-50' : 'bg-white/[0.03]'
	const detailUrl = SOURCE_DETAIL_URL[load.source]?.(load)

	const hasPickupWindow = load.pickupAppointmentStart || load.pickupAppointmentEnd
	const hasDeliveryWindow = load.deliveryAppointmentStart || load.deliveryAppointmentEnd
	const hasSchedule = hasPickupWindow || hasDeliveryWindow
	const stops = Array.isArray(load.stops) ? load.stops : []
	const orderedStops = stops.slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))

	return (
		<td colSpan={8} className={`px-6 py-4 rounded-lg ${expandedBg}`}>
			<div className="grid grid-cols-3 gap-6">
				<div>
					{orderedStops.length > 0 ? (
						<>
							<div
								className={`text-[10px] font-semibold tracking-wider uppercase mb-2 ${tokens.valueMuted}`}
							>
								Stops ({orderedStops.length})
							</div>
							<div className="space-y-3">
								{orderedStops.map((stop, idx) => {
									// Each stop renders in its own zone; fall back to the
									// origin zone for the first stop and the delivery zone
									// for the rest when a stop has no timezone.
									const stopTz =
										stop.timezone ||
										(idx === 0 ? load.originTimezone : load.deliveryTimezone)
									return (
										<StopCard
											key={`${stop.sequence ?? idx}-${stop.city ?? ''}`}
											stop={stop}
											tokens={tokens}
											isLight={isLight}
											timeZone={stopTz}
										/>
									)
								})}
							</div>
						</>
					) : hasSchedule ? (
						<Block title="Schedule">
							<Row
								label="Pickup"
								value={fmtApptWindow(
									load.pickupAppointmentStart,
									load.pickupAppointmentEnd,
									load.originTimezone,
								)}
							/>
							<Row
								label="Delivery"
								value={fmtApptWindow(
									load.deliveryAppointmentStart,
									load.deliveryAppointmentEnd,
									load.deliveryTimezone,
								)}
							/>
						</Block>
					) : (
						<Block title="Stops">
							<div className={`text-[12px] ${tokens.valueMuted}`}>—</div>
						</Block>
					)}
				</div>

				<Block title="Load">
					<Row label="Source ID" value={load.loadId} mono />
					<Row label="Reference #" value={load.referenceNumber} mono />
					<Row label="Type" value={load.loadType} mono />
					{load.hazmat != null && <Row label="Hazmat" value={load.hazmat ? 'Yes' : 'No'} />}
					<Row label="Commodity" value={load.commodity} />
					{detailUrl && (
						<div className="flex items-baseline gap-2 text-[12px] pt-1">
							<span className={`${tokens.valueMuted} w-16 shrink-0`}>Link</span>
							<a
								href={detailUrl}
								target="_blank"
								rel="noopener noreferrer"
								onClick={e => e.stopPropagation()}
								className="inline-flex items-center gap-1 text-sky-500 hover:text-sky-600 hover:underline"
							>
								Open on BrokerZone
								<ArrowTopRightOnSquareIcon className="h-3 w-3" />
							</a>
						</div>
					)}
				</Block>

				<Block title="Broker">
					<Row label="Name" value={load.brokerName} />
					<Row label="Email" value={load.brokerEmail} />
					<Row label="Phone" value={load.brokerPhone} />
				</Block>
			</div>

			{load.comments && (
				<div className="mt-4">
					<div
						className={`text-[10px] font-semibold tracking-wider uppercase mb-1.5 ${tokens.valueMuted}`}
					>
						Comments
					</div>
					<div className={`text-[12px] ${tokens.valueDim} whitespace-pre-wrap break-words`}>
						{load.comments}
					</div>
				</div>
			)}
		</td>
	)
}

export default LoadExpandedRow
